import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "core/store/authStore";
import { ROUTES } from "core/router/routes";
import apiClient from "core/api/client";
import {
  changeMyRole,
  deleteMyAccount,
  getMyProfile,
  updateMyProfile,
} from "features/user/services/userApi";
import type { AppRole } from "features/user/services/userApi";
import type { MeResponse } from "types/api.generated";

// ── Tab definitions ────────────────────────────────────────────────────────────

type Tab = "profile" | "security" | "role" | "danger";

const TABS: { id: Tab; label: string; icon: string; danger?: boolean }[] = [
  { id: "profile", label: "Profile", icon: "person" },
  { id: "security", label: "Security", icon: "lock" },
  { id: "role", label: "My Role", icon: "swap_horiz" },
  { id: "danger", label: "Danger Zone", icon: "warning", danger: true },
];

// ── Role cards data ────────────────────────────────────────────────────────────

const ROLES: {
  id: AppRole;
  label: string;
  tagline: string;
  description: string;
  icon: string;
  perks: string[];
}[] = [
  {
    id: "customer",
    label: "Customer",
    tagline: "Send packages & deliveries",
    description:
      "Create delivery jobs, track packages in real time, and manage your order history.",
    icon: "inventory_2",
    perks: [
      "Create delivery orders",
      "Real-time order tracking",
      "Order history & receipts",
    ],
  },
  {
    id: "provider",
    label: "Provider",
    tagline: "Deliver and earn on your schedule",
    description:
      "Register your vehicle, accept delivery jobs, and manage active deliveries.",
    icon: "directions_run",
    perks: [
      "Browse available jobs",
      "Vehicle verification",
      "Delivery history & stats",
    ],
  },
];

// ── Helper ────────────────────────────────────────────────────────────────────

function dashboardPath(role?: string, activeView?: string | null) {
  if (activeView === "customer") return ROUTES.CUSTOMER.DASHBOARD;
  if (role === "provider") return ROUTES.PROVIDER.DASHBOARD;
  return ROUTES.CUSTOMER.DASHBOARD;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AccountSettingsPage() {
  const { user, logout, activeView, setActiveView } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get("tab") as Tab | null) ?? "profile";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  // ── Profile tab state ──────────────────────────────────────────────────────
  const [profile, setProfile] = useState<MeResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    getMyProfile()
      .then((p) => {
        setProfile(p);
        setFirstName(p.firstName);
        setLastName(p.lastName ?? "");
      })
      .catch(() => {
        /* fail silently; form stays blank */
      })
      .finally(() => setProfileLoading(false));
  }, []);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setProfileError("First and last name are required.");
      return;
    }
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);
    try {
      await updateMyProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setProfileSuccess(true);
    } catch {
      setProfileError("Failed to save. Please try again.");
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Security tab state ─────────────────────────────────────────────────────
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleSendReset = async () => {
    if (!user?.email) return;
    setResetLoading(true);
    setResetError(null);
    try {
      await apiClient.post("/api/v1/auth/forgot-password", {
        email: user.email,
      });
      setResetSent(true);
    } catch {
      setResetError("Failed to send reset email. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  // ── Role tab state ─────────────────────────────────────────────────────────
  const currentRole = user?.role as AppRole | undefined;
  const [selectedRole, setSelectedRole] = useState<AppRole | null>(null);
  const [showRoleConfirm, setShowRoleConfirm] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const targetRole = ROLES.find((r) => r.id === selectedRole);

  const handleRoleSelect = (role: AppRole) => {
    if (role === currentRole) return;
    setSelectedRole(role);
    setRoleError(null);
    setShowRoleConfirm(true);
  };

  const handleRoleConfirm = async () => {
    if (!selectedRole) return;
    setRoleLoading(true);
    setRoleError(null);
    try {
      await changeMyRole(selectedRole);
      await logout();
      navigate("/login?reason=session_updated");
    } catch (err: any) {
      setRoleError(err?.message || "Failed to change role. Please try again.");
      setRoleLoading(false);
    }
  };

  // ── Danger tab state ───────────────────────────────────────────────────────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const DELETE_PHRASE = "delete my account";

  const handleDeleteAccount = async () => {
    if (deleteInput.toLowerCase() !== DELETE_PHRASE) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteMyAccount();
      await logout();
      navigate("/");
    } catch {
      setDeleteError("Failed to delete account. Please try again.");
      setDeleteLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 font-display">
      {/* ── Header ── */}
      <header className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary p-1.5 rounded-xl shadow-md shadow-primary/20">
                <span
                  className="material-symbols-outlined text-white text-[20px]"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  directions_run
                </span>
              </div>
              <span className="text-lg font-black tracking-tighter">
                Errandi
              </span>
            </Link>
            <span className="text-slate-300 text-lg">/</span>
            <span className="text-slate-600 font-medium text-sm">
              Account settings
            </span>
          </div>
          <Link
            to={dashboardPath(user?.role, activeView)}
            className="text-sm text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[16px]">
              arrow_back
            </span>
            Back to dashboard
          </Link>
        </div>
      </header>

      {/* ── Body ── */}
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex gap-8 items-start">
          {/* ── Sidebar ── */}
          <aside className="w-56 shrink-0 bg-white border border-slate-100 rounded-2xl p-3 sticky top-24">
            {/* User identity */}
            <div className="px-3 py-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                <span className="text-primary font-black text-base">
                  {(user?.displayName ?? user?.email ?? "?")[0].toUpperCase()}
                </span>
              </div>
              <p className="text-sm font-bold text-slate-800 leading-tight truncate">
                {user?.displayName ?? "My Account"}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <div className="border-t border-slate-100 my-2" />
            <nav className="flex flex-col gap-0.5">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className={`
                    w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors
                    ${
                      activeTab === tab.id
                        ? tab.danger
                          ? "bg-red-50 text-red-600"
                          : "bg-primary/8 text-primary"
                        : tab.danger
                          ? "text-red-400 hover:bg-red-50 hover:text-red-600"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }
                  `}
                >
                  <span
                    className="material-symbols-outlined text-[18px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* ── Content panel ── */}
          <div className="flex-1 min-w-0">
            {/* ── Profile ── */}
            {activeTab === "profile" && (
              <section className="bg-white border border-slate-100 rounded-2xl p-7">
                <h2 className="text-lg font-bold text-slate-900 mb-1">
                  Profile
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Update your personal information.
                </p>

                {profileLoading ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <span className="material-symbols-outlined text-[18px] animate-spin">
                      progress_activity
                    </span>
                    Loading…
                  </div>
                ) : (
                  <form onSubmit={handleProfileSave} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          First name
                        </label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => {
                            setFirstName(e.target.value);
                            setProfileSuccess(false);
                          }}
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          Last name
                        </label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            setProfileSuccess(false);
                          }}
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                          placeholder="Last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Email address
                      </label>
                      <input
                        type="email"
                        value={profile?.email ?? user?.email ?? ""}
                        disabled
                        className="w-full border border-slate-100 rounded-xl px-4 py-2.5 text-sm bg-slate-50 text-slate-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Email cannot be changed here.
                      </p>
                    </div>

                    {profileError && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                        {profileError}
                      </p>
                    )}
                    {profileSuccess && (
                      <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
                        <span
                          className="material-symbols-outlined text-[16px]"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          check_circle
                        </span>
                        Profile saved successfully.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={profileSaving}
                      className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-primary-deep transition-colors disabled:opacity-60"
                    >
                      {profileSaving ? (
                        <>
                          <span className="material-symbols-outlined text-[18px] animate-spin">
                            progress_activity
                          </span>{" "}
                          Saving…
                        </>
                      ) : (
                        "Save changes"
                      )}
                    </button>
                  </form>
                )}
              </section>
            )}

            {/* ── Security ── */}
            {activeTab === "security" && (
              <section className="bg-white border border-slate-100 rounded-2xl p-7">
                <h2 className="text-lg font-bold text-slate-900 mb-1">
                  Security
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Manage your password and account access.
                </p>

                <div className="border border-slate-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div>
                    <p className="font-semibold text-slate-800 text-sm mb-0.5">
                      Password
                    </p>
                    <p className="text-sm text-slate-500">
                      We'll send a reset link to <strong>{user?.email}</strong>.
                    </p>
                  </div>
                  {resetSent ? (
                    <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2 shrink-0">
                      <span
                        className="material-symbols-outlined text-[16px]"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        check_circle
                      </span>
                      Reset email sent!
                    </p>
                  ) : (
                    <button
                      onClick={handleSendReset}
                      disabled={resetLoading}
                      className="shrink-0 inline-flex items-center gap-2 border border-primary text-primary font-semibold px-5 py-2.5 rounded-xl hover:bg-primary/5 transition-colors disabled:opacity-60 text-sm"
                    >
                      {resetLoading ? (
                        <>
                          <span className="material-symbols-outlined text-[18px] animate-spin">
                            progress_activity
                          </span>{" "}
                          Sending…
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-[18px]">
                            send
                          </span>{" "}
                          Send reset email
                        </>
                      )}
                    </button>
                  )}
                </div>
                {resetError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 mt-3">
                    {resetError}
                  </p>
                )}
              </section>
            )}

            {/* ── My Role ── */}
            {activeTab === "role" && (
              <section>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">
                    My Role
                  </h2>
                  <p className="text-sm text-slate-500">
                    Temporarily try a different view, or permanently switch your account role.
                  </p>
                </div>

                {/* ── Quick switch (providers only) ── */}
                {user?.role === "provider" && (
                  <div className="bg-white border border-slate-100 rounded-2xl p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm mb-0.5">Active view</p>
                      <p className="text-sm text-slate-500">
                        {activeView === "customer"
                          ? "You're browsing as a Customer. Your account role is still Provider."
                          : "You're in Runner mode (your default view)."}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeView === "customer") {
                          setActiveView(null);
                          navigate(ROUTES.PROVIDER.DASHBOARD);
                        } else {
                          setActiveView("customer");
                          navigate(ROUTES.CUSTOMER.DASHBOARD);
                        }
                      }}
                      className="shrink-0 inline-flex items-center gap-2 border border-primary text-primary font-semibold px-5 py-2.5 rounded-xl hover:bg-primary/5 transition-colors text-sm"
                    >
                      <span
                        className="material-symbols-outlined text-[18px]"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        {activeView === "customer" ? "directions_run" : "inventory_2"}
                      </span>
                      {activeView === "customer" ? "Switch to Runner mode" : "Switch to Customer mode"}
                    </button>
                  </div>
                )}

                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Permanent role change</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {ROLES.map((role) => {
                    const isCurrent = role.id === currentRole;
                    const isSelected =
                      role.id === selectedRole && showRoleConfirm;
                    return (
                      <button
                        key={role.id}
                        onClick={() => handleRoleSelect(role.id)}
                        disabled={isCurrent}
                        className={`
                          text-left p-5 rounded-2xl border-2 transition-all
                          ${
                            isCurrent
                              ? "border-primary bg-primary/5 cursor-default"
                              : isSelected
                                ? "border-primary bg-primary/5 shadow-md shadow-primary/10"
                                : "border-slate-200 bg-white hover:border-primary/40 hover:shadow-sm cursor-pointer"
                          }
                        `}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className={`p-2.5 rounded-xl ${isCurrent || isSelected ? "bg-primary/10" : "bg-slate-100"}`}
                          >
                            <span
                              className={`material-symbols-outlined text-2xl ${isCurrent || isSelected ? "text-primary" : "text-slate-500"}`}
                              style={{ fontVariationSettings: '"FILL" 1' }}
                            >
                              {role.icon}
                            </span>
                          </div>
                          {isCurrent && (
                            <span className="text-xs font-semibold bg-primary text-white px-2.5 py-1 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-slate-900 mb-0.5">
                          {role.label}
                        </h3>
                        <p className="text-xs text-primary font-semibold mb-2">
                          {role.tagline}
                        </p>
                        <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                          {role.description}
                        </p>
                        <ul className="space-y-1">
                          {role.perks.map((perk) => (
                            <li
                              key={perk}
                              className="flex items-center gap-2 text-xs text-slate-600"
                            >
                              <span
                                className="material-symbols-outlined text-[14px] text-emerald-500"
                                style={{ fontVariationSettings: '"FILL" 1' }}
                              >
                                check_circle
                              </span>
                              {perk}
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>

                {showRoleConfirm && targetRole && (
                  <div className="bg-white border border-amber-200 rounded-2xl p-5">
                    <div className="flex gap-3">
                      <span
                        className="material-symbols-outlined text-amber-500 text-xl mt-0.5 shrink-0"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        warning
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800 mb-1">
                          Switch to{" "}
                          <span className="text-primary">
                            {targetRole.label}
                          </span>
                          ?
                        </p>
                        <p className="text-sm text-slate-500 mb-4">
                          Your current session will end and you'll be asked to
                          log in again. After logging in you'll be taken to the{" "}
                          {targetRole.label} dashboard.
                        </p>
                        {roleError && (
                          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
                            {roleError}
                          </p>
                        )}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleRoleConfirm}
                            disabled={roleLoading}
                            className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-primary-deep transition-colors disabled:opacity-60"
                          >
                            {roleLoading ? (
                              <>
                                <span className="material-symbols-outlined text-[18px] animate-spin">
                                  progress_activity
                                </span>{" "}
                                Switching…
                              </>
                            ) : (
                              <>
                                <span className="material-symbols-outlined text-[18px]">
                                  swap_horiz
                                </span>{" "}
                                Confirm switch
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowRoleConfirm(false);
                              setSelectedRole(null);
                            }}
                            className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* ── Danger Zone ── */}
            {activeTab === "danger" && (
              <section className="bg-white border border-red-100 rounded-2xl p-7">
                <h2 className="text-lg font-bold text-red-600 mb-1">
                  Danger Zone
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Irreversible actions. Please be certain.
                </p>

                {!showDeleteConfirm ? (
                  <div className="border border-red-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm mb-0.5">
                        Delete account
                      </p>
                      <p className="text-sm text-slate-500">
                        Permanently delete your account and all associated data.
                        This cannot be undone.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="shrink-0 inline-flex items-center gap-2 border border-red-300 text-red-600 font-semibold px-5 py-2.5 rounded-xl hover:bg-red-50 transition-colors text-sm"
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        delete_forever
                      </span>
                      Delete account
                    </button>
                  </div>
                ) : (
                  <div className="border border-red-200 rounded-2xl p-5 bg-red-50/40">
                    <p className="font-semibold text-slate-800 mb-1">
                      Are you absolutely sure?
                    </p>
                    <p className="text-sm text-slate-500 mb-4">
                      This will permanently delete your account and all data.
                      Type{" "}
                      <strong className="text-red-600">
                        "{DELETE_PHRASE}"
                      </strong>{" "}
                      to confirm.
                    </p>
                    <input
                      type="text"
                      value={deleteInput}
                      onChange={(e) => setDeleteInput(e.target.value)}
                      placeholder={DELETE_PHRASE}
                      className="w-full border border-red-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 transition mb-4 bg-white"
                    />
                    {deleteError && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
                        {deleteError}
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={
                          deleteInput.toLowerCase() !== DELETE_PHRASE ||
                          deleteLoading
                        }
                        className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
                      >
                        {deleteLoading ? (
                          <>
                            <span className="material-symbols-outlined text-[18px] animate-spin">
                              progress_activity
                            </span>{" "}
                            Deleting…
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined text-[18px]">
                              delete_forever
                            </span>{" "}
                            Permanently delete
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteInput("");
                          setDeleteError(null);
                        }}
                        className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
